/* The one place that says "a walkthrough build must never change live data".
 *
 * A walkthrough build (`--mode walkthrough`, see `.env.walkthrough`) is for
 * demo sessions. It may READ the live API, but it must never WRITE to it: no
 * ticket, no reply, no "seen" mark, no push subscription. Every browser call
 * that can change live data goes through `guardedFetch`, or checks
 * `assertLiveWritesAllowed()` first when it needs to fail before any other work.
 *
 * The check runs before the request is built, so a blocked write makes no
 * network call at all. `app/scripts/smoke.mjs` proves that against the real
 * walkthrough bundle, with a fetch that counts its calls.
 *
 * A new call that writes to /api and does NOT use this file is the thing to
 * catch in review. `app/test/liveWrites.test.js` scans for it.
 */

export const WALKTHROUGH_WRITE_BLOCKED = 'WALKTHROUGH_WRITE_BLOCKED';

export const isWalkthroughBuild = () => import.meta.env?.VITE_WALKTHROUGH_DEMO === 'true';

export function walkthroughWriteError() {
  const error = new Error('Walkthrough mode cannot change real data. Nothing was sent. Use the normal app to do this for real.');
  error.code = WALKTHROUGH_WRITE_BLOCKED;
  return error;
}

/** Throws before any network call when this is a walkthrough build. */
export function assertLiveWritesAllowed({ walkthrough = isWalkthroughBuild() } = {}) {
  if (walkthrough) throw walkthroughWriteError();
}

/** `fetch`, except that a walkthrough build refuses anything but a read. */
export function guardedFetch(url, init, { walkthrough = isWalkthroughBuild() } = {}) {
  const method = String(init?.method || 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') {
    try {
      assertLiveWritesAllowed({ walkthrough });
    } catch (error) {
      return Promise.reject(error);
    }
  }
  return fetch(url, init);
}
